import React, { useState, useEffect } from "react";
import {
  Authenticator,
  useTheme,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { View, Image, Text, Heading, Button } from "@aws-amplify/ui-react";
import { Auth } from "aws-amplify";

const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View
        textAlign="center"
        padding={tokens.space.large}
        // style={{ height: "200px" }}
      >
        <Image
          alt="Amplify logo"
          style={{ width: "70%" }}
          src={process.env.PUBLIC_URL + "/CC Logo (Black Background).png"}
        />
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text color={tokens.colors.neutral[10]}>
          &copy; All Rights Reserved
        </Text>
      </View>
    );
  },

  SignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Sign in
        </Heading>
      );
    },
    Footer() {
      const { toForgotPassword } = useAuthenticator();

      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={toForgotPassword}
            size="small"
            variation="link"
          >
            Reset Password
          </Button>
        </View>
      );
    },
  },

  // SignUp component is hidden - registration is disabled
  // Users must be invited by an administrator
  SignUp: {
    Header() {
      const { tokens } = useTheme();

      return (
        <View
          textAlign="center"
          padding={tokens.space.xl}
          style={{
            backgroundColor: "#1a1a1a",
            borderRadius: "8px",
            margin: tokens.space.medium,
          }}
        >
          <Heading
            padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
            level={3}
            color={tokens.colors.neutral[90]}
          >
            Registration Disabled
          </Heading>
          <View padding={tokens.space.large}>
            <Text
              fontSize={tokens.fontSizes.medium}
              color={tokens.colors.neutral[80]}
            >
              Please contact an administrator to create an account.
            </Text>
            <Text
              fontSize={tokens.fontSizes.small}
              color={tokens.colors.neutral[60]}
              padding={tokens.space.medium}
            >
              New users must be invited by an admin.
            </Text>
          </View>
        </View>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();
      const { tokens } = useTheme();

      return (
        <View textAlign="center" padding={tokens.space.medium}>
          <Button
            fontWeight="normal"
            onClick={toSignIn}
            size="small"
            variation="primary"
          >
            Back to Sign In
          </Button>
        </View>
      );
    },
  },
  ConfirmSignUp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  SetupTotp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  ConfirmSignIn: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  ForgotPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  ConfirmResetPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
};

const formFields = {
  signIn: {
    username: {
      placeholder: "Enter your email",
    },
  },
  signUp: {
    password: {
      label: "Password:",
      placeholder: "Enter your Password:",
      isRequired: false,
      order: 2,
    },
    confirm_password: {
      label: "Confirm Password:",
      order: 1,
    },
  },
  forceNewPassword: {
    password: {
      placeholder: "Enter your Password:",
    },
  },
  forgotPassword: {
    username: {
      placeholder: "Enter your email:",
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      placeholder: "Enter your Confirmation Code:",
      label: "New Label",
      isRequired: false,
    },
    confirm_password: {
      placeholder: "Enter your Password Please:",
    },
  },
  setupTotp: {
    QR: {
      totpIssuer: "test issuer",
      totpUsername: "amplify_qr_test_user",
    },
    confirmation_code: {
      label: "New Label",
      placeholder: "Enter your Confirmation Code:",
      isRequired: false,
    },
  },
  confirmSignIn: {
    confirmation_code: {
      label: "New Label",
      placeholder: "Enter your Confirmation Code:",
      isRequired: false,
    },
  },
};

export default function CustomAuthenticator({
  isAuthenticated,
  provider,
  children,
}) {
  const { tokens } = useTheme();
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const [isIOS, setIsIOS] = useState(false);

  // Check if running on iOS device on component mount
  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isIOSDevice =
      userAgent.includes("iPad") || userAgent.includes("iPhone");
    console.log("CustomAuthenticator on device:", userAgent);
    console.log("Is iOS device:", isIOSDevice);
    setIsIOS(isIOSDevice);

    // On iOS, we need to ensure we reset authentication for a fresh start
    if (isIOSDevice) {
      console.log(
        "iOS device detected in CustomAuthenticator - preparing login page"
      );

      // Clear all authentication prevention flags when we're explicitly on the login page
      localStorage.removeItem("authRetryPrevented");
      localStorage.removeItem("authRetryTimestamp");

      // Force clear any Amplify cache that might be causing issues
      try {
        // Use localstorage to track that we're on the login page
        localStorage.setItem("onLoginPage", "true");
      } catch (e) {
        console.error("Error updating localStorage:", e);
      }
    }

    return () => {
      // When component unmounts, remove the login page indicator
      localStorage.removeItem("onLoginPage");
    };
  }, []);

  // Customize the header to include provider information if available
  const Header = () => {
    // Check if provider has a valid logo (not null, undefined, or empty string)
    const hasProviderLogo = provider?.logoUrl && provider.logoUrl.trim() !== "";
    const logoSrc = hasProviderLogo
      ? provider.logoUrl
      : process.env.PUBLIC_URL + "/CC Logo (Black Background).png";

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Image
          alt={hasProviderLogo ? "Company Logo" : "Catalyst and Core Logo"}
          style={{ width: "70%" }}
          src={logoSrc}
          onError={(e) => {
            // Fall back to default logo on error
            e.target.src =
              process.env.PUBLIC_URL + "/CC Logo (Black Background).png";
            e.target.alt = "Catalyst and Core Logo";
          }}
        />
        {provider && (
          <Heading level={4} padding={tokens.space.medium}>
            {provider.companyName}
          </Heading>
        )}
      </View>
    );
  };

  // Update the components object to use the new Header
  const updatedComponents = {
    ...components,
    Header,
  };

  return (
    <View
      style={{
        backgroundColor: isAuthenticated ? "white" : "black",
        flex: 1,
        minHeight: "100vh",
        width: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Ensure iOS devices have proper sizing
        ...(isIOS
          ? {
              height: "100vh",
              maxHeight: "-webkit-fill-available",
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }
          : {}),
      }}
    >
      <Authenticator
        initialState="signIn"
        formFields={formFields}
        components={updatedComponents}
        hide={["SignUp", "SignUp.fields"]}
        style={{ backgroundColor: "black" }}
        backgroundColor="black"
        socialProviders={["facebook", "google", "amazon"]}
      >
        {(params) => {
          console.log("Render function called with auth params:", params);
          const { user } = params;
          console.log("Authenticated user:", user);
          if (user) {
            // Clear the retry prevention flag when successfully authenticated
            localStorage.removeItem("authRetryPrevented");
            localStorage.removeItem("authRetryTimestamp");
            localStorage.removeItem("onLoginPage");
            return children;
          }
          return null;
        }}
      </Authenticator>
    </View>
  );
}
